import React, { useState } from 'react'
import { Layout, Menu, Button, Dropdown, Drawer } from 'antd'
import { Link, useLocation } from 'react-router-dom'
import { GlobalOutlined, MenuOutlined } from '@ant-design/icons'
import { useLanguage } from '../../contexts/LanguageContext'
import { useSiteSettings } from '../../contexts/SiteSettingsContext'
import { useTranslation } from 'react-i18next'

const { Header: AntHeader } = Layout

const Header: React.FC = () => {
  const { language, changeLanguage } = useLanguage()
  const { settings, getLocalizedText } = useSiteSettings()
  const { t } = useTranslation()
  const location = useLocation()
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false)

  // Navigation menu items
  const menuItems = [
    { key: '/home-products', label: t('navigation.homeProducts'), path: '/home-products', external: false },
    { key: '/medical-products', label: t('navigation.medicalProducts'), path: '/medical-products', external: false },
    { key: '/stores', label: t('navigation.stores'), path: '/stores', external: false },
    { key: '/online-shop', label: t('navigation.onlineShop'), path: 'https://www.newstarltd.com.hk/%E6%AD%90%E5%A7%86%E9%BE%8D-omron', external: true },
    { key: '/service', label: t('navigation.service'), path: '/service', external: false },
    { key: '/about', label: t('navigation.aboutUs'), path: '/about', external: false },
  ]

  // Language switcher dropdown
  const languageMenu = {
    items: [
      {
        key: 'en',
        label: 'English',
        onClick: () => changeLanguage('en'),
      },
      {
        key: 'zh',
        label: '繁體中文',
        onClick: () => changeLanguage('zh'),
      },
    ],
  }

  const renderDesktopMenu = () => (
    <Menu
      theme="light"
      mode="horizontal"
      selectedKeys={[location.pathname]}
      className="flex-1 justify-center border-none text-lg"
      items={menuItems.map(item => ({
        key: item.key,
        label: item.external ? (
          <a href={item.path} target="_blank" rel="noopener noreferrer" className="text-lg">
            {item.label}
          </a>
        ) : (
          <Link to={item.path} className="text-lg">{item.label}</Link>
        ),
      }))}
    />
  )

  const renderMobileMenu = () => (
    <Menu
      mode="vertical"
      selectedKeys={[location.pathname]}
      className="text-lg"
      items={menuItems.map(item => ({
        key: item.key,
        label: item.external ? (
          <a href={item.path} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuVisible(false)} className="text-lg">
            {item.label}
          </a>
        ) : (
          <Link to={item.path} onClick={() => setMobileMenuVisible(false)} className="text-lg">
            {item.label}
          </Link>
        ),
      }))}
    />
  )

  const getCurrentLanguageLabel = () => {
    return language === 'zh' ? '繁' : 'EN'
  }

  return (
    <AntHeader className="bg-white shadow-header px-0">
      <div className="container mx-auto">
        <div className="flex items-center justify-between h-16 px-gutter">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              {settings?.logo?.url ? (
                <img
                  src={settings.logo.url}
                  alt={getLocalizedText(settings.logo.alt)}
                  className="h-10 w-auto"
                  style={{ maxHeight: '40px', height: 'auto' }}
                />
              ) : (
                <span className="text-2xl font-bold text-primary">
                  {settings?.siteName ? getLocalizedText(settings.siteName) : 'OMRON'}
                </span>
              )}
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex flex-1 items-center justify-between ml-8">
            {renderDesktopMenu()}
            
            {/* Language Switcher */}
            <Dropdown menu={languageMenu} placement="bottomRight">
              <Button 
                type="text" 
                icon={<GlobalOutlined />}
                className="flex items-center gap-1"
              >
                {getCurrentLanguageLabel()}
              </Button>
            </Dropdown>
          </div>

          {/* Mobile Menu Button & Language Switcher */}
          <div className="md:hidden flex items-center gap-2">
            <Dropdown menu={languageMenu} placement="bottomRight">
              <Button 
                type="text" 
                icon={<GlobalOutlined />}
                size="small"
              >
                {getCurrentLanguageLabel()}
              </Button>
            </Dropdown>
            
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileMenuVisible(true)}
              className="text-lg"
            />
          </div>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <Drawer
        title={
          settings?.logo?.url ? (
            <div className="flex items-center">
              <img
                src={settings.logo.url}
                alt={getLocalizedText(settings.logo.alt)}
                style={{ height: '32px', width: 'auto' }}
              />
            </div>
          ) : (
            settings?.siteName ? getLocalizedText(settings.siteName) : 'OMRON'
          )
        }
        placement="right"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={280}
        styles={{ body: { padding: 0 } }}
      >
        {renderMobileMenu()}
      </Drawer>
    </AntHeader>
  )
}

export default Header 